import * as actions from './actions';
import createResourceStatusReducer from 'lib/createResourceStatusReducer';
import getIn from '@alexaivars/get-in';
import { getSite } from '../global/reducers';
import { NAME } from './constants';
import { STATES } from 'lib/constants';
import { combineReducers } from 'redux';
import { DIALOGS } from './constants';
import { ADD_FOLLOWING } from 'modules/following/actions';
import { SHOW_CREATE_ALERT_OVERLAY } from 'modules/search/actions';

const EMPTY_STATE = {};

export function createValueReducer(type, key) {
  return (state = null, action = {}) =>
    action.type === type && action.payload && action.payload.key === key
      ? action.payload.value
      : state;
}

const createResourceErrorsReducer = (baseActions, resetActions) => {
  const failure = []
    .concat(baseActions)
    .map(action => `${action}_${STATES.FAILURE}`);

  const reset = [].concat(resetActions);

  return (state = [], action = {}) => {
    if (reset.includes(action.type)) {
      return [];
    }
    if (failure.includes(action.type)) {
      return getIn(action, ['error', 'errors'], []);
    }
    return state;
  };
};

export const isJournalist = state => {
  const roles = getIn(state, [NAME, 'profile', 'roles'], []);
  return Boolean(roles.find(item => item === 'journalist'));
};
export const isPressOfficer = state => {
  const roles = getIn(state, [NAME, 'profile', 'roles'], []);
  return Boolean(roles.find(item => item === 'press_officer'));
};
export const isAdmin = state => {
  const roles = getIn(state, [NAME, 'profile', 'roles'], []);
  return Boolean(roles.find(item => item === 'administrator'));
};

export const isAuthenticated = state =>
  Boolean(getIn(state, [NAME, 'profile', 'sub'], false));
export const getUserProfile = state => getIn(state, [NAME, 'profile'], {});

export const getNotLoggedInAction = state =>
  getIn(state, [NAME, 'notLoggedInAction']);
export const getDialog = state => getIn(state, [NAME, 'dialogs', 'type']);
export const hasDialog = state => Boolean(getDialog(state));
export const getLocale = state =>
  getIn(state, [NAME, 'profile', 'locale'], getSite(state).defaultLocale);
export const getSignupEmail = state =>
  getIn(state, [NAME, 'dialogs', 'input', 'email']) || '';
export const getSignupPassword = state =>
  getIn(state, [NAME, 'dialogs', 'input', 'password']) || '';
export const getSignupValues = state => ({
  email: getSignupEmail(state),
  password: getSignupPassword(state),
});
export const getDialogCallback = state =>
  getIn(state, [NAME, 'dialogs', 'callback']);
export const getTermsStatus = state =>
  getIn(state, [NAME, 'dialogs', 'terms', 'status']);
export const getResetStatus = state =>
  getIn(state, [NAME, 'dialogs', 'reset', 'status']);
export const getResetErrors = state =>
  getIn(state, [NAME, 'dialogs', 'reset', 'errors']);
export const getLoginStatus = state =>
  getIn(state, [NAME, 'dialogs', 'login', 'status']);
export const getLoginErrors = state =>
  getIn(state, [NAME, 'dialogs', 'login', 'errors']);
export const getSignupStatus = state =>
  getIn(state, [NAME, 'dialogs', 'signup', 'status']);
export const getSignupErrors = state =>
  getIn(state, [NAME, 'dialogs', 'signup', 'errors']);
export const getUnverifiedEmail = state =>
  getIn(state, [NAME, 'dialogs', 'unverified', 'email']);
export const getTermsText = state => getIn(state, [NAME, 'terms', 'text']);
export const getTermsId = state => getIn(state, [NAME, 'terms', 'id']);

const dialogs = (state = EMPTY_STATE, action) =>
  action.type === actions.HIDE_DIALOG ||
  action.type === actions.LOGIN_SUCCESS ||
  action.type === actions.RESTORE
    ? EMPTY_STATE
    : combineReducers({
        // callback action to run when complete
        callback: (state = null, action) =>
          action.type === actions.SHOW_DIALOG
            ? action.payload.callback || null
            : state,
        // which dialog is currently active
        type: (state = null, action) => {
          switch (action.type) {
            case actions.CONFIRM_FAILURE:
              return DIALOGS.VERIFICATION;
            case actions.SHOW_DIALOG:
              return action.payload.type;
            default:
              return state;
          }
        },
        // input text persistent between multiple dialogues
        input: combineReducers({
          email: createValueReducer(actions.UPDATE_INPUT, 'email'),
          password: createValueReducer(actions.UPDATE_INPUT, 'password'),
        }),
        // we might be able to simplify this if we are
        // only ever interested in loading state.
        login: combineReducers({
          status: createResourceStatusReducer(
            actions.LOGIN,
            actions.UPDATE_INPUT,
          ),
          errors: createResourceErrorsReducer(
            actions.LOGIN,
            actions.UPDATE_INPUT,
          ),
        }),
        reset: combineReducers({
          status: createResourceStatusReducer(
            actions.RESET,
            actions.UPDATE_INPUT,
          ),
          errors: createResourceErrorsReducer(
            actions.RESET,
            actions.UPDATE_INPUT,
          ),
        }),
        signup: combineReducers({
          status: createResourceStatusReducer(
            actions.SIGNUP,
            actions.UPDATE_INPUT,
          ),
          errors: createResourceErrorsReducer(
            actions.SIGNUP,
            actions.UPDATE_INPUT,
          ),
        }),
        terms: combineReducers({
          status: createResourceStatusReducer(
            actions.LOAD_TERMS,
            actions.UPDATE_INPUT,
          ),
          errors: createResourceErrorsReducer(
            actions.LOAD_TERMS,
            actions.UPDATE_INPUT,
          ),
        }),
        unverified: combineReducers({
          email: createValueReducer(actions.SAVE_UNVERIFIED_EMAIL),
        }),
      })(state, action);

const reducer = combineReducers({
  dialogs,
  terms: (state = null, action) => {
    switch (action.type) {
      case actions.LOAD_TERMS_SUCCESS:
        return {
          text: action.payload.body[0].content,
          id: action.payload.body[0].id,
        };
      case actions.LOAD_TERMS_FAILURE:
        return null;
      default:
        return state;
    }
  },
  profile: (state = null, action) => {
    switch (action.type) {
      case actions.RESTORE:
      case actions.SET_USER_COOKIES_DATA:
        const { sub, email, roles, locale } = action.payload;
        return {
          ...state,
          email,
          locale,
          roles,
          sub,
        };
      case actions.LOGIN_FAILURE:
      case actions.LOGOUT_SUCCESS:
      case actions.LOGOUT_FAILURE:
        return null;
      default:
        return state;
    }
  },
  notLoggedInAction: (state = {}, action) => {
    switch (action.type) {
      case actions.SAVE_FOLLOW_DATA:
        return {
          action: 'follow',
          id: action.payload.id,
        };

      case actions.SAVE_CREATE_ALERT_CLICK:
        return {
          action: 'alert',
        };
      case actions.HIDE_DIALOG:
      case actions.SIGNUP_SUCCESS:
      case actions.RESET_SUCCESS:
      case `${ADD_FOLLOWING}_${STATES.SUCCESS}`:
      case SHOW_CREATE_ALERT_OVERLAY:
        return {};
      default:
        return state;
    }
  },
});

export default function(state = EMPTY_STATE, action = {}) {
  // Reset to starting state when user logges out.
  // if (action.type === actions.LOGOUT_SUCCESS) {
  // return reducer(EMPTY_STATE, action);
  // }

  return reducer(state, action);
}
